import {
  PickerAcceptFnFile,
  PickerAcceptFnOptions,
  PickerOptions
} from "./types"

/**
 * Filestack API Key
 * Consolidated from multiple locations to single source of truth
 */
export const FILESTACK_API_KEY = "PKIcKga_QLKv1rkAdPQB"

/**
 * Filestack styles URL for preview
 */
export const FILESTACK_STYLES_URL =
  "https://duointeractive.github.io/filestack-styles.css"

/**
 * Media Types
 * Consolidated from legacy constants
 */
export const MEDIA_TYPES = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  FILE: "FILE",
  AUDIO: "AUDIO",
  DOCUMENT: "DOCUMENT",
  ARCHIVE: "ARCHIVE",
  ANY: "ANY",
  LINK: "LINK"
} as const

export type MediaType = (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES]

/**
 * Media Extensions Map
 * Maps media types to their file extensions
 */
export const MEDIA_EXTENSIONS_MAP: { [key in MediaType]?: string[] } = {
  [MEDIA_TYPES.IMAGE]: [
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".bmp",
    ".docx",
    ".heic"
  ],
  [MEDIA_TYPES.VIDEO]: [".mp4", ".mpeg", ".mov"],
  [MEDIA_TYPES.DOCUMENT]: [".doc", ".pdf", ".pages", ".rtf", ".docx"],
  [MEDIA_TYPES.ARCHIVE]: [".zip", ".tar", ".gz", ".rar"],
  [MEDIA_TYPES.FILE]: [],
  [MEDIA_TYPES.AUDIO]: [".mp3"],
  [MEDIA_TYPES.ANY]: []
}

/**
 * Raster image MIME list for Filestack `accept` (excludes SVG — treated as files).
 * Keep in sync with `IMAGE_FILESTACK_ACCEPT` in `@pathwright/media-utils`.
 */
const IMAGE_FILESTACK_ACCEPT: string[] = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/x-ms-bmp",
  "image/tiff",
  "image/x-icon",
  "image/vnd.microsoft.icon",
  "image/heic",
  "image/heif",
  "image/avif",
  ".heic"
]

/**
 * Media Type Map
 * Maps media types to their MIME type accept patterns for Filestack
 */
export const MEDIA_TYPE_MAP: { [key in MediaType]?: string[] } = {
  [MEDIA_TYPES.IMAGE]: IMAGE_FILESTACK_ACCEPT,
  [MEDIA_TYPES.VIDEO]: ["video/*"],
  [MEDIA_TYPES.FILE]: ["application/*", "text/*", "image/svg+xml", ".svg", ".svgz"],
  [MEDIA_TYPES.DOCUMENT]: MEDIA_EXTENSIONS_MAP[MEDIA_TYPES.DOCUMENT],
  [MEDIA_TYPES.ARCHIVE]: MEDIA_EXTENSIONS_MAP[MEDIA_TYPES.ARCHIVE],
  [MEDIA_TYPES.AUDIO]: [
    "audio/*",
    "audio/aac",
    "audio/midi",
    "audio/mpeg",
    "audio/mp4",
    "audio/ogg",
    "audio/opus",
    "audio/wav",
    "audio/webm",
    "audio/x-m4a",
    "audio/3gpp"
  ],
  [MEDIA_TYPES.ANY]: undefined
}

const SVG_MIMETYPE = "image/svg+xml"

const isSvgMimetype = (mimetype = ""): boolean =>
  mimetype.toLowerCase().startsWith(SVG_MIMETYPE)

const isSvgFilename = (filename = ""): boolean => /\.svgz?$/i.test(filename)

const getPickerOriginalFilename = (file: PickerAcceptFnFile): string =>
  file.source === "local_file_system" && file.originalFile
    ? file.originalFile instanceof File
      ? file.originalFile.name
      : file.originalFile?.name || file.filename
    : file.filename

/**
 * Base Filestack Options
 * Common options shared across all media types
 */
export const BASE_FILESTACK_OPTIONS: PickerOptions = {
  imageMax: [2000, 2000],
  maxFiles: 5,
  uploadInBackground: false,
  fromSources: [
    "local_file_system",
    "dropbox",
    "googledrive",
    "onedrive",
    "facebook"
  ],
  disableDirectoryUpload: true
}

/**
 * Filestack Options by Media Type
 * Consolidated from legacy and media-utils implementations
 */
export const FILESTACK_OPTIONS: { [key in MediaType]?: PickerOptions } = {
  [MEDIA_TYPES.DOCUMENT]: {
    ...BASE_FILESTACK_OPTIONS,
    accept: MEDIA_TYPE_MAP[MEDIA_TYPES.DOCUMENT],
    maxSize: 100 * 1024 * 1024,
    maxFiles: 1
  },
  [MEDIA_TYPES.ARCHIVE]: {
    ...BASE_FILESTACK_OPTIONS,
    accept: MEDIA_TYPE_MAP[MEDIA_TYPES.ARCHIVE],
    maxSize: 100 * 1024 * 1024,
    maxFiles: 1
  },
  [MEDIA_TYPES.IMAGE]: {
    ...BASE_FILESTACK_OPTIONS,
    fromSources: [
      ...BASE_FILESTACK_OPTIONS.fromSources!,
      "instagram",
      "unsplash",
      "webcam",
      "imagesearch"
    ],
    customSourceName: "Sample Images",
    accept: MEDIA_TYPE_MAP[MEDIA_TYPES.IMAGE],
    acceptFn: async (
      file: PickerAcceptFnFile,
      options: PickerAcceptFnOptions
    ): Promise<string> => {
      const originalFileName = getPickerOriginalFilename(file)

      let mimeTest = ""

      if (file.source === "local_file_system" && file.originalFile) {
        const magicMime =
          file.originalFile instanceof File
            ? await options.mimeFromMagicBytes(file.originalFile)
            : ""
        const extMime = options.mimeFromExtension(originalFileName)
        mimeTest = magicMime || extMime
      } else if (file.mimetype) {
        mimeTest = file.mimetype
      } else {
        mimeTest = options.mimeFromExtension(file.filename)
      }

      if (isSvgMimetype(mimeTest) || isSvgFilename(originalFileName)) {
        return Promise.reject(
          "Please upload a supported raster image file instead of an SVG."
        )
      }

      return Promise.resolve("")
    },
    maxSize: 100 * 1024 * 1024,
    maxFiles: 20
  },
  [MEDIA_TYPES.FILE]: {
    ...BASE_FILESTACK_OPTIONS,
    accept: undefined,
    maxSize: 100 * 1024 * 1024,
    maxFiles: 15
  },
  [MEDIA_TYPES.VIDEO]: {
    ...BASE_FILESTACK_OPTIONS,
    fromSources: [...BASE_FILESTACK_OPTIONS.fromSources!, "video", "url"],
    accept: MEDIA_TYPE_MAP[MEDIA_TYPES.VIDEO],
    maxSize: 500 * 1024 * 1024
  },
  [MEDIA_TYPES.AUDIO]: {
    ...BASE_FILESTACK_OPTIONS,
    fromSources: [...BASE_FILESTACK_OPTIONS.fromSources!, "audio", "url"],
    accept: MEDIA_TYPE_MAP[MEDIA_TYPES.AUDIO],
    // Note: filestack-js types are incorrect for acceptFn - mimeFromMagicBytes and mimeFromExtension
    // are actually functions, not the types shown in the definitions
    acceptFn: async (
      file: PickerAcceptFnFile,
      options: PickerAcceptFnOptions
    ): Promise<string> => {
      // This custom accept function added specifically to permit the m4a file type;
      // For some reason the magicbytes validation doesn't recognize it
      // https://www.filestack.com/docs/uploads/pickers/web/#acceptFn

      let mimeTest = ""
      const originalFileName = getPickerOriginalFilename(file)

      // Local File system has the unique .originalFile key
      if (file.source === "local_file_system" && file.originalFile) {
        // mimeFromMagicBytes is a function that returns Promise<string>
        // Only call if originalFile is actually a File instance
        const magicMime =
          file.originalFile instanceof File
            ? await options.mimeFromMagicBytes(file.originalFile)
            : ""
        // mimeFromExtension is a function that takes a filename and returns string
        const extMime = options.mimeFromExtension(originalFileName)
        mimeTest = magicMime.indexOf("audio/") >= 0 ? magicMime : extMime
        // Use mimeType if it's set
      } else if (file.mimetype) {
        mimeTest = file.mimetype
        // Fall back to Filestack's built in extension detector
      } else {
        mimeTest = options.mimeFromExtension(file.filename)
      }

      if (mimeTest.indexOf("audio/") === -1) {
        return Promise.reject(
          `Please upload a valid audio file. Detected file type: ${file.mimetype}.`
        )
      }

      // Note: filestack-js types incorrectly expect Promise<string> but runtime accepts Promise<void>
      // Return empty string to satisfy type checker
      return Promise.resolve("")
    },
    maxSize: 100 * 1024 * 1024
  } as PickerOptions,
  [MEDIA_TYPES.ANY]: {
    ...BASE_FILESTACK_OPTIONS,
    accept: undefined,
    fromSources: [
      ...BASE_FILESTACK_OPTIONS.fromSources!,
      "instagram",
      "unsplash",
      "webcam",
      "imagesearch",
      "audio",
      "url"
    ],
    maxSize: 500 * 1024 * 1024
  }
}
